const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Types
export interface Property {
  id: string;
  title: string;
  description?: string;
  address?: string;
  city: string;
  region?: string;
  country: string;
  latitude?: number;
  longitude?: number;
  property_type?: string;
  total_area_sqm?: number;
  warehouse_area_sqm?: number;
  clear_height_m?: number;
  num_docks?: number;
  floor_load_kg_sqm?: number;
  listing_type?: string;
  rent_eur_sqm?: number;
  sale_price_eur?: number;
  availability_status?: string;
  available_from?: string;
  listing_url?: string;
  broker_name?: string;
  broker_company?: string;
  first_seen_at?: string;
  last_seen_at?: string;
}

export interface PropertyDetail extends Property {
  broker_email?: string;
  broker_phone?: string;
  broker_website?: string;
  price_history?: PriceHistoryEntry[];
  related_listings?: RelatedListing[];
}

export interface PriceHistoryEntry {
  rent_eur_sqm?: number;
  sale_price_eur?: number;
  recorded_at: string;
  source?: string;
}

export interface RelatedListing {
  id: string;
  source_url?: string;
  listing_url?: string;
  broker_company?: string;
  rent_eur_sqm?: number;
  last_seen_at?: string;
}

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
}

export interface PropertiesResponse {
  data: Property[];
  pagination: Pagination;
}

export interface Alert {
  id: string;
  name?: string;
  filter_criteria: Record<string, unknown>;
  notify_email: boolean;
  notify_frequency: string;
  is_active: boolean;
  created_at: string;
}

export interface CreateAlertRequest {
  name?: string;
  filter_criteria: Record<string, unknown>;
  notify_frequency?: string;
}

export interface HealthResponse {
  status: string;
  timestamp: string;
  environment: string;
  version: string;
  services: {
    d1: boolean;
    kv: boolean;
    r2: boolean;
  };
}

export interface StatsResponse {
  by_country_type: {
    country: string;
    property_type: string;
    count: number;
    avg_rent: number;
    avg_size: number;
    avg_height: number;
  }[];
  totals: {
    total_properties: number;
    total_cities: number;
    total_brokers: number;
  };
}

// API methods
export const api = {
  health: () => fetchApi<HealthResponse>('/health'),

  properties: {
    list: (params?: Record<string, string>) => {
      const query = params ? `?${new URLSearchParams(params)}` : '';
      return fetchApi<PropertiesResponse>(`/properties${query}`);
    },
    get: (id: string) => fetchApi<PropertyDetail>(`/properties/${id}`),
    stats: () => fetchApi<StatsResponse>('/properties/stats/overview'),
  },

  alerts: {
    list: () => fetchApi<{ data: Alert[] }>('/alerts'),
    create: (data: CreateAlertRequest) =>
      fetchApi<Alert>('/alerts', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<CreateAlertRequest>) =>
      fetchApi<Alert>(`/alerts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => fetchApi<void>(`/alerts/${id}`, { method: 'DELETE' }),
  },
};
